import { Button, Collapse, IconButton, Paper, Stack, TextField, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { com } from '@eidu/form'
import ItemList from './ItemList'
import { withKey } from '../../util/withKey'
import FormKeyField from './FormKeyField'
import NextElementDisplay from '../data/NextElementDisplay'
import NextElementInput from './NextElementInput'
import { LocalNext, localNextFromNext, nextFromLocalNext } from '../../domain/form/definition/LocalNext'
import { useOtherPages } from './OtherPagesProvider'
import Page = com.eidu.sharedlib.form.definition.Page

type FormPageProps = {
  page: Page
  onInsertAfter: () => void
  onDelete?: () => void
  onChange?: (currentKey: string, newPage: Page) => void
  isExpanded: boolean
  onExpand: () => void
}

const FormPage: React.FC<FormPageProps> = ({ page, onInsertAfter, onDelete, onChange, isExpanded, onExpand }) => {
  const pageKey = page.key ?? ''

  const otherPages = useOtherPages()

  return (
    <Stack spacing={1}>
      <Paper
        elevation={1}
        sx={{
          p: 2,
          borderRadius: '4px',
          bgcolor: 'grey.50',
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
              <IconButton
                size="small"
                onClick={onExpand}
                aria-expanded={isExpanded}
                aria-label="toggle page expansion"
                sx={{
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 200ms',
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
              {onChange ? (
                <>
                  <TextField
                    value={page.title}
                    onChange={(e) => onChange(pageKey, withKey(page.copy(e.target.value), page))}
                    variant="standard"
                    fullWidth
                  />
                  <FormKeyField
                    value={pageKey}
                    onChange={(e) =>
                      onChange(pageKey, withKey(page.copy(page.title, page.items, e.target.value), page))
                    }
                    disabled={false}
                  />
                </>
              ) : (
                <Typography variant="subtitle1">{page.title}</Typography>
              )}
            </Stack>
            {!onChange ? (
              <NextElementDisplay
                value={page.nextPage ? localNextFromNext(page.nextPage) : undefined}
                options={otherPages}
                type={'page'}
              />
            ) : (
              <NextElementInput
                value={page.nextPage ? localNextFromNext(page.nextPage) : undefined}
                parentKey={pageKey}
                type={'page'}
                options={otherPages}
                onChange={(newValue: LocalNext | undefined) =>
                  onChange(
                    pageKey,
                    withKey(
                      page.copy(page.title, page.items, page.key, newValue ? nextFromLocalNext(newValue) : null),
                      page
                    )
                  )
                }
              />
            )}
            {onDelete && (
              <IconButton aria-label="delete page" onClick={onDelete} size="small">
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>

          <Collapse in={isExpanded} timeout={200}>
            <ItemList
              items={page.items}
              onChange={onChange && ((newItems) => onChange(pageKey, withKey(page.copy(page.title, newItems), page)))}
            />
          </Collapse>
        </Stack>
      </Paper>

      {onChange && (
        <Button variant="outlined" size="small" onClick={onInsertAfter} sx={{ alignSelf: 'center' }}>
          Insert Page
        </Button>
      )}
    </Stack>
  )
}

export default FormPage
